import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { AccessLevelIcon } from '@dbt-labs/dbt-dag';
import { DropdownButton, DropdownOption, Sizes, Tooltip } from '@dbt-labs/sourdough';

import { AccessLevel, ModelVersion } from '@/__generated__/graphql';
import { useLinkToExplorer } from '@/hooks/links';
import { useProjectScope } from '@/hooks/params';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';

interface ModelNameProps {
  uniqueId: ParsedUniqueId;
  version: string | undefined | null;
  accessLevel?: AccessLevel | null;
  versions?: ModelVersion[];
}

export const ModelNameWithAccessIcon: FC<Omit<ModelNameProps, 'version'>> = ({
  accessLevel,
  uniqueId,
  versions,
}) => {
  const showIcon = !!accessLevel;
  const shouldShowVersions = versions && (versions?.length ?? 0) > 1;

  return (
    <span className="flex h-10 w-full items-center pr-4">
      <span
        className={`-mt-2 h-6 w-7 duration-300 motion-reduce:duration-0 ${showIcon ? 'opacity-100' : 'opacity-0'}`}
      >
        {showIcon && (
          <AccessLevelIcon
            size="md"
            access={accessLevel}
            tooltipConfig={{ placement: 'bottom' }}
            iconClassName="h-6 w-6 text-fgMain"
          />
        )}
      </span>
      <div className="relative w-full rounded-md p-2">
        <div className="relative inline-block max-w-full pr-8 text-fgMain">
          {shouldShowVersions && (
            <ModelVersionDropdown uniqueId={uniqueId} versions={versions} />
          )}
          {!shouldShowVersions && (
            <Tooltip
              displayOnlyWhenTruncated
              content={uniqueId.remainder}
              className="w-full"
              childrenAreInteractive={false}
            >
              {(ref) => (
                <div
                  ref={ref}
                  className="pointer-events-none truncate whitespace-nowrap"
                >
                  <h1 className="text-3xl text-fgMain">{uniqueId.remainder}</h1>
                </div>
              )}
            </Tooltip>
          )}
        </div>
      </div>
    </span>
  );
};

interface ModelVersionDropdownProps {
  uniqueId: ParsedUniqueId;
  versions: ModelVersion[];
}

export const ModelVersionDropdown: FC<ModelVersionDropdownProps> = ({
  uniqueId,
  versions,
}) => {
  const { accountId, projectId, environmentType } = useProjectScope();

  const navigate = useNavigate();
  const getResourceUrl = useLinkToExplorer('resourceDetails');

  const versionOptions = useMemo((): DropdownOption[] => {
    return (
      versions?.map((version) => {
        const { remainder } = parseUniqueId(version.uniqueId);
        return {
          label: `${remainder} (${version.releaseVersion})`,
          value: version.uniqueId,
        };
      }) ?? []
    );
  }, [versions]);

  return (
    <DropdownButton
      testId="model-versions-dropdown"
      onChange={(version) => {
        navigate(
          getResourceUrl({
            dbtUniqueId: version.value as string,
            accountId,
            environmentType,
            projectId,
          }),
        );
      }}
      defaultOption={versionOptions.find((v) => v.value === uniqueId.raw)}
      className={twMerge(
        'h-14 w-max bg-opacity-0 px-4 py-2.5 text-2xl text-opacity-0 shadow-none',
        'border border-borderMuted border-opacity-0',
        'hover:bg-opacity-40 active:bg-opacity-40',
      )}
      size={Sizes.xl}
      menuPlacement="bottom"
      listClassName="right-0"
      options={versionOptions}
    />
  );
};
